/**
 * Purpose:
 *   Provide toast notification state and actions via React context.
 */
'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { ToastViewport } from '../components/ToastViewport';

export type ToastType = 'error' | 'warning' | 'success';

export interface ToastInput {
  message: string;
  type: ToastType;
  duration?: number;
}

export interface ToastInternal {
  id: string;
  message: string;
  type: ToastType;
  duration: number;
  createdAt: number;
}

export interface ToastContextValue {
  addToast: (input: ToastInput) => string;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<ToastInternal[]>([]);
  const [mounted, setMounted] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    // False positive: This setState is safe and necessary for SSR hydration check.
    // It runs only once on mount to ensure createPortal is only used after client-side hydration,
    // preventing hydration mismatches in Next.js. This is a common and recommended pattern.
    // eslint-disable-next-line -- React Compiler false positive: setState in effect is intentional for SSR hydration
    setMounted(true);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback((input: ToastInput) => {
    const id = `t_${Date.now()}_${seq.current++}`;
    const rec: ToastInternal = {
      id,
      message: input.message,
      type: input.type,
      duration:
        typeof input.duration === 'number' && input.duration > 0
          ? input.duration
          : 5000,
      createdAt: Date.now(),
    };
    setToasts((prev) => [rec, ...prev]);
    return id;
  }, []);

  const value = useMemo(
    () => ({ addToast, removeToast }),
    [addToast, removeToast]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {mounted &&
        createPortal(
          <ToastViewport toasts={toasts} onDismiss={removeToast} />,
          document.body
        )}
    </ToastContext.Provider>
  );
};

export function useToastContext(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx)
    throw new Error('useToastContext must be used within <ToastProvider>.');
  return ctx;
}
